import React, { useEffect, useMemo, useRef } from 'react';
import { Calendar, Star, Flag, Users, Utensils, Coffee, FlagTriangleRight, MapPin, Globe } from 'lucide-react';
import { useAccessibility } from '../contexts/AccessibilityContext';
import { useListNavigation } from '../hooks/useListNavigation';
import './Timetable.css';

// Static conference schedule — mirrors the iOS app's hardcoded
// TimetableView.swift data exactly, so both apps show the same schedule.
const conferenceSchedule = [
  {
    date: 'Sunday 13 September',
    sections: [
      {
        title: 'Pre-Conference',
        timeRange: '13:00 – 19:00',
        style: 'preConference',
        events: [
          { time: '13:00 – 14:00', title: 'Virtual Annual Meeting of Associate Members', location: 'Online', mappable: false },
          { time: '14:30 – 17:00', title: 'AAC User Workshop', location: 'Riley Smith Theatre', mappable: true },
          { time: '14:30 – 17:00', title: 'Relaxed Exhibition', location: 'Refectory', mappable: true },
          { time: '18:00 – 19:00', title: 'AAC User / Trustee Dinner', location: 'Refectory', mappable: true },
        ],
      },
    ],
  },
  {
    date: 'Monday 14 September',
    sections: [
      {
        title: 'Conference Opens',
        timeRange: '09:00 – 11:30',
        style: 'opening',
        events: [
          { time: '09:00 – 11:30', title: 'Registration with Tea / Coffee', location: 'LUU Foyer', mappable: true },
          { time: '11:10 – 18:00', title: 'Exhibition Open · Posters Displayed 10:30 – 17:00', location: 'Refectory & LUU Foyer', mappable: true },
          {
            time: '09:30 – 10:00',
            title: "MEETING 1: Newbie's Icebreaker Meeting",
            subtitle: 'For everyone attending for the first time. Hosted by Helen Hewson and Saffron Murphy-Mann, Trustees.',
            location: 'Esther Simpson, LG08',
            mappable: true,
          },
          {
            time: '10:00 – 10:10',
            title: 'WELCOME: Andrea Sharples, Chair of Communication Matters',
            location: 'Esther Simpson, LG08',
            mappable: true,
            highlight: true,
          },
          {
            time: '10:15 – 11:00',
            title: 'KEYNOTE: Jeff Higginbotham',
            subtitle: 'Looking at AAC Through Time and Interaction',
            location: 'Esther Simpson, LG08',
            mappable: true,
            highlight: true,
          },
          {
            time: '10:30 – 17:00',
            title: 'Poster Exhibition',
            subtitle: 'Vote for your favourite and enter into the CM Book Draw',
            location: 'Riley Smith',
            mappable: true,
          },
        ],
      },
      { title: 'Session 1', timeRange: '11:15 – 12:00', style: 'session', events: [] },
      { title: 'Lunch', timeRange: '12:00 – 13:20', style: 'lunch', events: [] },
      { title: 'Session 2', timeRange: '13:20 – 14:05', style: 'session', events: [] },
      { title: 'Session 3', timeRange: '14:15 – 15:00', style: 'session', events: [] },
      { title: 'Break', timeRange: '15:00 – 15:30', style: 'break', events: [] },
      { title: 'Session 4', timeRange: '15:30 – 16:15', style: 'session', events: [] },
      { title: 'Session 5', timeRange: '16:25 – 17:10', style: 'session', events: [] },
      {
        title: '40th Anniversary Celebratory Dinner',
        timeRange: '18:30 – 23:00',
        style: 'dinner',
        events: [
          { time: '18:30', title: 'Travel from University of Leeds to Royal Armouries' },
          { time: '18:30', title: 'Drinks Reception', location: 'Royal Armouries', mappable: true },
          {
            time: '19:30',
            title: '40th Anniversary Celebratory Dinner',
            subtitle: 'Featuring Lee Ridley, DJs and 1986 Fancy Dress (optional)',
            location: 'Royal Armouries',
            mappable: true,
            highlight: true,
          },
          { time: '23:00', title: 'Carriages' },
        ],
      },
    ],
  },
  {
    date: 'Tuesday 15 September',
    sections: [
      {
        title: 'Final Words',
        timeRange: '09:00 – 10:00',
        style: 'closing',
        events: [
          {
            time: '09:00 – 09:10',
            title: 'FINAL WORDS: Andrea Sharples, Chair of Communication Matters',
            location: 'Esther Simpson, LG08',
            mappable: true,
            highlight: true,
          },
          {
            time: '09:10 – 10:00',
            title: 'PLENARY: Gavin Clifton',
            subtitle: "The Disabled Writer: Children's author, inspirational speaker, disability advocate, lyricist.",
            location: 'Esther Simpson, LG08',
            mappable: true,
            highlight: true,
          },
        ],
      },
      { title: 'Session 6', timeRange: '10:15 – 11:00', style: 'session', events: [] },
      { title: 'Break', timeRange: '11:00 – 11:40', style: 'break', events: [] },
      { title: 'Session 7', timeRange: '11:40 – 12:25', style: 'session', events: [] },
      { title: 'Lunch', timeRange: '12:25 – 13:35', style: 'lunch', events: [] },
      { title: 'Session 8', timeRange: '13:35 – 14:20', style: 'session', events: [] },
      { title: 'Session 9', timeRange: '14:30 – 15:15', style: 'session', events: [] },
    ],
  },
];

const SECTION_STYLES = {
  preConference: { icon: Star, colorClass: 'accent-purple' },
  opening: { icon: Flag, colorClass: 'accent-blue' },
  session: { icon: Users, colorClass: 'accent-grey' },
  lunch: { icon: Utensils, colorClass: 'accent-green' },
  break: { icon: Coffee, colorClass: 'accent-orange' },
  dinner: { icon: Utensils, colorClass: 'accent-gold' },
  closing: { icon: FlagTriangleRight, colorClass: 'accent-blue' },
};

const Timetable = () => {
  const { announce, focusFirstHeading } = useAccessibility();

  useEffect(() => {
    focusFirstHeading();
    announce('Conference timetable page loaded');
  }, [announce, focusFirstHeading]);

  // Flat, page-order list of every scheduled event (across all days/sections)
  // so N/P keyboard navigation and Tab order move through the whole
  // timetable top to bottom, matching the pattern used on other list pages.
  const flatEventList = useMemo(() => {
    const list = [];
    conferenceSchedule.forEach((day, dayIndex) => {
      day.sections.forEach((section, sectionIndex) => {
        section.events.forEach((event, eventIndex) => {
          list.push({
            ...event,
            id: `${dayIndex}-${sectionIndex}-${eventIndex}`,
            dayDate: day.date,
            sectionTitle: section.title,
          });
        });
      });
    });
    return list;
  }, []);

  const { currentItem: currentNavEvent } = useListNavigation(flatEventList, {
    getLabel: (event) => `${event.dayDate}, ${event.sectionTitle}, ${event.time}, ${event.title}`,
    getId: (event) => event.id,
  });
  const currentNavEventId = currentNavEvent ? currentNavEvent.id : null;

  const eventRefs = useRef(new Map());
  useEffect(() => {
    if (!currentNavEventId) return;
    const el = eventRefs.current.get(currentNavEventId);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    el.focus({ preventScroll: true });
  }, [currentNavEventId]);

  return (
    <div className="timetable-page">
      <div className="timetable-summary-header">
        <div>
          <h1>Conference Schedule</h1>
          <p className="timetable-summary-subtitle">University of Leeds · 13–15 September 2026</p>
        </div>
        <Calendar size={26} className="timetable-summary-icon" aria-hidden="true" />
      </div>

      {conferenceSchedule.map((day, dayIndex) => (
        <section key={day.date} className="timetable-day" aria-labelledby={`day-${dayIndex}-heading`}>
          <h2 id={`day-${dayIndex}-heading`} className="timetable-day-banner">
            {day.date.toUpperCase()}
          </h2>

          {day.sections.map((section, sectionIndex) => {
            const { icon: Icon, colorClass } = SECTION_STYLES[section.style] || SECTION_STYLES.session;
            const isDinner = section.style === 'dinner';

            return (
              <React.Fragment key={section.title}>
                {sectionIndex > 0 && <hr className="timetable-section-divider" />}
                <div className={`timetable-section ${isDinner ? 'timetable-section--dinner' : ''}`}>
                  <div className="timetable-section-header">
                    <span className={`timetable-section-badge ${colorClass}`} aria-hidden="true">
                      <Icon size={14} />
                    </span>
                    <div>
                      <h3 className="timetable-section-title">{section.title}</h3>
                      <p className="timetable-section-time">{section.timeRange}</p>
                    </div>
                  </div>

                  {section.events.length > 0 && (
                    <ul className="timetable-event-card">
                      {section.events.map((event, eventIndex) => {
                        const eventId = `${dayIndex}-${sectionIndex}-${eventIndex}`;
                        const isCurrent = currentNavEventId === eventId;
                        return (
                          <li
                            key={eventId}
                            ref={(el) => {
                              if (el) eventRefs.current.set(eventId, el);
                              else eventRefs.current.delete(eventId);
                            }}
                            tabIndex={0}
                            className={`timetable-event-row ${event.highlight ? 'timetable-event-row--highlight' : ''} ${isCurrent ? 'list-nav-current' : ''}`}
                            aria-label={`${event.time}, ${event.title}${event.subtitle ? `, ${event.subtitle}` : ''}${event.location ? `, ${event.location}` : ''}`}
                          >
                            <span className="timetable-event-time">{event.time}</span>
                            <p className={`timetable-event-title ${event.highlight ? 'timetable-event-title--highlight' : ''}`}>
                              {event.title}
                            </p>
                            {event.subtitle && (
                              <p className="timetable-event-subtitle">{event.subtitle}</p>
                            )}
                            {event.location && (
                              <span className={`timetable-location-chip ${event.mappable ? 'timetable-location-chip--mappable' : 'timetable-location-chip--plain'}`}>
                                {event.mappable ? <MapPin size={11} aria-hidden="true" /> : <Globe size={11} aria-hidden="true" />}
                                {event.location}
                              </span>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </React.Fragment>
            );
          })}
        </section>
      ))}
    </div>
  );
};

export default Timetable;
